import { useEffect, useRef, useState } from 'react';
import { Icons } from '../components/Icons.jsx';
import { useAuth } from '../lib/auth.jsx';
import { useI18n, useT } from '../lib/i18n.jsx';
import { useToast } from '../lib/toast.jsx';
import { errorMessage } from '../lib/errors.js';
import * as api from '../lib/api.js';
import { GOOGLE_CLIENT_ID, renderGoogleButton } from '../lib/google.js';

// Mirrors otpEmailCooldown in the backend's modauth/otp.go — asking sooner
// just earns a 429.
const RESEND_SECONDS = 60;

export default function Login() {
  const t = useT();
  const { lang, setLang, isRtl } = useI18n();
  const toast = useToast();
  const { signInWithGoogle, signInWithOtp, signInWithPassword } = useAuth();

  // 'email' collects the address, 'code' collects what we mailed them.
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Email + password is the default door. The emailed code is the fallback,
  // one tap away, for accounts that came from Google/Apple and never set a
  // password — which is what `login.passwordHint` below spells out.
  const [usePassword, setUsePassword] = useState(true);
  const [code, setCode] = useState('');
  const [otpId, setOtpId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [googleError, setGoogleError] = useState(!GOOGLE_CLIENT_ID);

  const googleRef = useRef(null);
  const codeRef = useRef(null);
  const passwordRef = useRef(null);

  // --- Google button -------------------------------------------------
  // Re-rendered on language change so its own label follows the portal.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleRef.current) return;

    let cancelled = false;
    const host = googleRef.current;
    host.innerHTML = '';

    renderGoogleButton(host, {
      locale: lang,
      onCredential: async (idToken) => {
        if (cancelled) return;
        setBusy(true);
        try {
          await signInWithGoogle(idToken);
        } catch (err) {
          toast.error(errorMessage(err, t));
          setBusy(false);
        }
      },
    }).catch(() => {
      if (!cancelled) setGoogleError(true);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // --- resend cooldown -----------------------------------------------
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  async function submitPassword(e) {
    e?.preventDefault();
    const address = email.trim();
    if (!address || !password) return;

    setBusy(true);
    try {
      await signInWithPassword(address, password);
      // Success routes away; no need to unset busy.
    } catch (err) {
      toast.error(errorMessage(err, t));
      setPassword('');
      passwordRef.current?.focus();
      setBusy(false);
    }
  }

  async function sendCode(e) {
    e?.preventDefault();
    const address = email.trim();
    if (!address) return;

    setBusy(true);
    try {
      const res = await api.requestOtp(address, lang);
      setOtpId(res?.otp_id ?? null);
      setStep('code');
      setCode('');
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      toast.error(errorMessage(err, t));
    } finally {
      setBusy(false);
    }
  }

  async function verify(e) {
    e?.preventDefault();
    const entered = code.trim();
    if (!otpId || entered.length < 6) return;

    setBusy(true);
    try {
      await signInWithOtp(otpId, entered);
      // On success the route swaps to the dashboard — no need to unset busy.
    } catch (err) {
      toast.error(errorMessage(err, t));
      setCode('');
      codeRef.current?.focus();
      setBusy(false);
    }
  }

  return (
    <div className="login-page">
      <button
        type="button"
        className="lang-toggle floating"
        onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
      >
        <Icons.Globe size={16} />
        <span>{lang === 'ar' ? 'English' : 'العربية'}</span>
      </button>

      <div className="login-card">
        <div className="login-brand">
          <span className="brand-mark large" aria-hidden="true">
            <Icons.Book size={24} />
          </span>
          <strong>{t('brand.name')}</strong>
        </div>

        {step === 'email' ? (
          <>
            <h1>{t('login.title')}</h1>
            <p className="login-sub">{t('login.subtitle')}</p>

            {googleError ? (
              <p className="login-note">{t('login.googleUnavailable')}</p>
            ) : (
              <div className="google-host" ref={googleRef} />
            )}

            <div className="or-rule"><span>{t('login.or')}</span></div>

            <form onSubmit={usePassword ? submitPassword : sendCode} className="login-form">
              <label className="field">
                <span>{t('login.emailLabel')}</span>
                <input
                  className="input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  dir="ltr"
                  placeholder={t('login.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>

              {usePassword && (
                <label className="field">
                  <span>{t('login.passwordLabel')}</span>
                  <input
                    ref={passwordRef}
                    className="input"
                    type="password"
                    autoComplete="current-password"
                    dir="ltr"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </label>
              )}

              <button
                type="submit"
                className="btn primary block"
                disabled={busy || !email.trim() || (usePassword && !password)}
              >
                {usePassword
                  ? (busy ? t('login.verifying') : t('login.signIn'))
                  : (busy ? t('login.sending') : t('login.sendCode'))}
              </button>
            </form>

            <button
              type="button"
              className="btn ghost block"
              onClick={() => { setUsePassword((v) => !v); setPassword(''); }}
              disabled={busy}
            >
              {usePassword ? t('login.useCodeInstead') : t('login.usePasswordInstead')}
            </button>

            {usePassword && <p className="login-hint">{t('login.passwordHint')}</p>}
          </>
        ) : (
          <>
            <h1>{t('login.codeTitle')}</h1>
            <p className="login-sub">{t('login.codeSent', { email: email.trim() })}</p>

            <form onSubmit={verify} className="login-form">
              <label className="field">
                <span>{t('login.codeLabel')}</span>
                <input
                  ref={codeRef}
                  className="input code-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  dir="ltr"
                  maxLength={6}
                  pattern="[0-9]*"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                />
              </label>
              <button type="submit" className="btn primary block" disabled={busy || code.length < 6}>
                {busy ? t('login.verifying') : t('login.verify')}
              </button>
            </form>

            <div className="login-actions">
              <button
                type="button"
                className="btn ghost"
                onClick={sendCode}
                disabled={busy || cooldown > 0}
              >
                {cooldown > 0 ? t('login.resendIn', { seconds: cooldown }) : t('login.resend')}
              </button>
              <button
                type="button"
                className="btn ghost"
                onClick={() => { setStep('email'); setOtpId(null); setCode(''); }}
                disabled={busy}
              >
                {t('login.changeEmail')}
              </button>
            </div>
          </>
        )}

        <p className="login-legal">
          {t('login.footerNote')}{' '}
          <a href={isRtl ? '/ar/tos-ar.html' : '/tos.html'}>{t('login.terms')}</a>
          {' · '}
          <a href={isRtl ? '/ar/privacypolicy-ar.html' : '/privacypolicy.html'}>{t('login.privacy')}</a>
        </p>
      </div>
    </div>
  );
}
